import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTeamContext } from '@/hooks/useTeamContext'
import { USER_ROLES } from '@/config/permissions'
import { missionService } from '@/services/missionService'
import { isFreelancerMissionActionDisplayStatus } from '@/constants/missionStatus'
import { isFreelancerExtensionGraceNotificationMission } from '@/utils/freelancerExtensionGraceNotification'
import { activityReportService } from '@/services/activityReportService'
import { useCompanyNotificationMissionsQuery } from '@/hooks/queries/useCompanyNotificationMissionsQuery'
import { useCommentUnreadSummary } from '@/hooks/queries/useResourceComments'
import { useApiReady } from '@/hooks/useApiReady'
import type { UnreadCommentSummaryItem } from '@/services/resourceCommentService'
import {
  isMissionNotificationEligible,
  isMissionCompanyNotificationRecipient,
  missionNeedsCompanyNotificationAction,
} from '@/utils/missionCompanyNotification'

export interface NotificationItem {
  id: string
  type: 'mission' | 'activity-report' | 'comment'
  title: string
  description: string
  path: string
  freelancer?: string
  month?: string
  /** Statut d’affichage mission (badges liste missions) — renseigné pour type mission */
  displayStatus?: string
  /** Côté entreprise : addendum vs autre document pour le libellé du centre de notif */
  companySignatureContext?: 'addendum' | 'mission_document' | 'mission_approval'
  /** Contexte ressource pour les notifications de commentaires */
  commentContext?: 'mission' | 'activity_report'
}

function mapMissionsToNotificationItems(
  list: Record<string, unknown>[],
  isFreelancer: boolean,
  userId: string
): NotificationItem[] {
  return list
    .filter((mission) => {
      const displayStatus =
        (mission.displayStatus as string) ||
        (mission.display_status as string) ||
        ''

      if (isFreelancer) {
        if (isFreelancerExtensionGraceNotificationMission(mission, displayStatus)) {
          return true
        }
        if (!isMissionNotificationEligible(displayStatus)) return false
        if (!isFreelancerMissionActionDisplayStatus(displayStatus)) return false
      } else if (!isMissionNotificationEligible(displayStatus)) {
        return false
      } else if (!missionNeedsCompanyNotificationAction(mission, displayStatus)) {
        return false
      }

      if (!isFreelancer) {
        return isMissionCompanyNotificationRecipient(mission, userId, displayStatus)
      }
      return true
    })
    .map((mission) => {
      const displayStatus =
        (mission.displayStatus as string) ||
        (mission.display_status as string) ||
        ''
      const pendCoAdd = Number(
        mission.pending_addenda_count_for_company ?? mission.pendingAddendaCountForCompany ?? 0
      )
      const titleRaw = (mission.title ?? mission.name) as string | undefined
      const freelancerLine =
        (mission.freelancer as string) ||
        (typeof mission.freelancer === 'object' && mission.freelancer !== null
          ? [
              (mission.freelancer as { first_name?: string }).first_name,
              (mission.freelancer as { last_name?: string }).last_name,
            ]
              .filter(Boolean)
              .join(' ')
              .trim()
          : '') ||
        ''
      return {
        id: String(mission.id),
        type: 'mission' as const,
        title: titleRaw && String(titleRaw).trim() ? String(titleRaw) : '--',
        description: isFreelancer ? freelancerLine : freelancerLine,
        path: `/missions/${mission.id}`,
        freelancer: freelancerLine,
        displayStatus,
        companySignatureContext: !isFreelancer
          ? displayStatus === 'pending-approval'
            ? 'mission_approval'
            : pendCoAdd > 0
              ? 'addendum'
              : 'mission_document'
          : undefined,
      }
    })
}

function mapUnreadCommentsToNotificationItems(
  items: UnreadCommentSummaryItem[]
): NotificationItem[] {
  return items.map((item) => {
    const freelancerLine = [item.freelancerFirstName, item.freelancerLastName]
      .filter(Boolean)
      .join(' ')
      .trim()
    const isMission = item.resourceType === 'mission'

    return {
      id: `comment-${item.resourceType}-${item.resourceId}`,
      type: 'comment' as const,
      commentContext: item.resourceType,
      title: item.missionName && item.missionName.trim() ? item.missionName : '--',
      description: item.lastCommentBody,
      path: isMission
        ? `/missions/${item.resourceId}`
        : `/activity-report/view/${item.resourceId}`,
      freelancer: freelancerLine || undefined,
      month: item.activityReportMonth ?? undefined,
    }
  })
}

interface UseNotificationItemsReturn {
  items: NotificationItem[]
  missionItems: NotificationItem[]
  activityReportItems: NotificationItem[]
  commentItems: NotificationItem[]
  totalCount: number
  missionsCount: number
  activityReportsCount: number
  commentsCount: number
  isLoading: boolean
}

export function useNotificationItems(options?: { enabled?: boolean }): UseNotificationItemsReturn {
  const { effectiveRole, user, activeTeamId, teams } = useTeamContext()
  const apiReady = useApiReady()
  const fetchEnabled = options?.enabled !== false
  const isFreelancer = effectiveRole === USER_ROLES.FREELANCER
  const notificationTeamId = activeTeamId ?? teams[0]?.id ?? undefined

  const { data: companyMissionsRaw = [], isLoading: companyMissionsLoading } =
    useCompanyNotificationMissionsQuery({ enabled: fetchEnabled })

  const { data: freelancerMissionItems = [], isLoading: freelancerMissionsLoading } = useQuery({
    queryKey: ['notificationItems', 'missions', 'freelancer', user?.id, notificationTeamId],
    queryFn: async (): Promise<NotificationItem[]> => {
      if (!user?.id) return []

      try {
        const result = await missionService.getMissions({
          page: 1,
          pageSize: 100,
          freelancerId: user.id,
          teamId: notificationTeamId,
          sortBy: 'updated_at',
          sortDirection: 'desc',
        })

        if (!result?.success || !result?.data?.list) return []

        return mapMissionsToNotificationItems(
          result.data.list as Record<string, unknown>[],
          true,
          user.id
        )
      } catch {
        return []
      }
    },
    enabled: apiReady && fetchEnabled && isFreelancer && !!notificationTeamId,
    staleTime: 60 * 1000,
    refetchInterval: 3 * 60 * 1000,
    retry: 0,
  })

  const missionItems = useMemo(() => {
    if (!user?.id) return []
    if (isFreelancer) return freelancerMissionItems
    return mapMissionsToNotificationItems(companyMissionsRaw, false, user.id)
  }, [
    isFreelancer,
    user?.id,
    freelancerMissionItems,
    companyMissionsRaw,
  ])

  const missionsLoading = isFreelancer ? freelancerMissionsLoading : companyMissionsLoading

  const { data: unreadComments = [], isLoading: commentsLoading } = useCommentUnreadSummary(
    notificationTeamId,
    user?.id,
    { enabled: fetchEnabled && !!notificationTeamId }
  )

  const commentItems = useMemo(
    () => mapUnreadCommentsToNotificationItems(unreadComments),
    [unreadComments]
  )

  const { data: activityReportItems = [], isLoading: reportsLoading } =
    useQuery({
      queryKey: [
        'notificationItems',
        'activityReports',
        user?.id,
        effectiveRole,
        activeTeamId,
        notificationTeamId,
      ],
      queryFn: async (): Promise<NotificationItem[]> => {
        if (!user?.id || isFreelancer) return []

        try {
          const result = await activityReportService.getReports({
            page: 1,
            pageSize: 100,
            status: 'pending',
            teamId: notificationTeamId,
          })

          if (!result?.success || !result?.data?.list) return []

          return result.data.list
            .filter((report: Record<string, unknown>) => {
              const reportStatus = String(
                (report.status as string) || ''
              ).toLowerCase()
              if (reportStatus !== 'pending') return false
              const hasExternalReferent = !!(report as Record<string, unknown>).hasExternalReferent
              if (hasExternalReferent) return false
              return (
                report.referentId != null && report.referentId === user.id
              )
            })
            .map((report: Record<string, unknown>) => ({
              id: report.id as string,
              type: 'activity-report' as const,
              title: report.mission as string,
              description: report.freelancer as string,
              path: `/activity-report/view/${report.id}`,
              freelancer: report.freelancer as string,
              month: report.month as string,
            }))
        } catch {
          return []
        }
      },
      enabled: apiReady && fetchEnabled && !isFreelancer && !!notificationTeamId,
      staleTime: 60 * 1000,
      refetchInterval: 3 * 60 * 1000,
      retry: 0,
    })

  const missionTabItems = useMemo(
    () => [
      ...missionItems,
      ...commentItems.filter((item) => item.commentContext === 'mission'),
    ],
    [missionItems, commentItems]
  )

  const activityReportTabItems = useMemo(
    () => [
      ...activityReportItems,
      ...commentItems.filter((item) => item.commentContext === 'activity_report'),
    ],
    [activityReportItems, commentItems]
  )

  const allItems = [...missionItems, ...activityReportItems, ...commentItems]

  return {
    items: allItems,
    missionItems: missionTabItems,
    activityReportItems: activityReportTabItems,
    commentItems,
    totalCount: allItems.length,
    missionsCount: missionTabItems.length,
    activityReportsCount: activityReportTabItems.length,
    commentsCount: commentItems.length,
    isLoading: missionsLoading || reportsLoading || commentsLoading,
  }
}
